import React from 'react'
import { ArrowLeft, AlertTriangle, CalendarClock, Loader2, ListChecks } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { SocialPost } from '@/features/social/types'
import { cn } from '@/lib/utils'

export type CommandBarProfile = {
  id: string
  name: string
  platform?: string
  avatar?: string | null
}

const STATUS_STYLES: Record<SocialPost['status'], string> = {
  DRAFT: 'bg-amber-100 text-amber-800',
  SCHEDULED: 'bg-blue-100 text-blue-800',
  PROCESSING: 'bg-purple-100 text-purple-800',
  POSTED: 'bg-emerald-100 text-emerald-800',
  ERROR: 'bg-red-100 text-red-800',
  QUEUED: 'bg-slate-100 text-slate-800'
}

interface CreatePostCommandBarProps {
  title: string
  profileName?: string
  profileAvatar?: string | null
  platform?: string
  queueLabel?: string
  scheduleLabel?: string
  status: SocialPost['status']
  hasValidationErrors?: boolean
  validationMessage?: string
  isSubmitting?: boolean
  disablePublish?: boolean
  showSaveDraft?: boolean
  onBack?: () => void
  onCancel: () => void
  onSaveDraft?: () => void
  onPublish: () => void
  profiles?: CommandBarProfile[]
}

export function CreatePostCommandBar({
  title,
  profileName,
  profileAvatar,
  platform,
  queueLabel,
  scheduleLabel,
  status,
  hasValidationErrors,
  validationMessage,
  isSubmitting,
  disablePublish,
  showSaveDraft,
  onBack,
  onCancel,
  onSaveDraft,
  onPublish,
  profiles
}: CreatePostCommandBarProps) {
  const statusBadge = STATUS_STYLES[status] ?? STATUS_STYLES.DRAFT
  const profileList = profiles && profiles.length > 0
    ? profiles
    : profileName
      ? [{ id: 'primary', name: profileName, platform, avatar: profileAvatar ?? undefined }]
      : []
  const initials = (name?: string | null) => {
    if (!name) return 'SP'
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }
  const primaryProfile = profileList[0]
  const profileLabel = profileList.length > 1
    ? `${primaryProfile?.name ?? 'Select profiles'} +${profileList.length - 1} more`
    : primaryProfile?.name || profileName || 'Select a profile'
  const platformBadges = profileList.length > 0
    ? Array.from(new Set(profileList.map(p => p.platform).filter(Boolean))) as string[]
    : platform
      ? [platform]
      : []

  return (
    <div className="sticky -mt-4 top-0 z-20 border-b border-border/60 bg-background/90 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md">
      <div className="flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            <div className="flex items-center gap-3">
              {profileList.length > 1 ? (
                <div className="flex -space-x-2">
                  {profileList.slice(0, 3).map(profile => (
                    <Avatar key={profile.id} className="h-10 w-10 border-2 border-background shadow-sm">
                      {profile.avatar ? (
                        <AvatarImage src={profile.avatar} alt={profile.name} />
                      ) : (
                        <AvatarFallback>{initials(profile.name)}</AvatarFallback>
                      )}
                    </Avatar>
                  ))}
                  {profileList.length > 3 && (
                    <div className="h-10 w-10 rounded-full border-2 border-background bg-muted text-xs font-semibold text-muted-foreground flex items-center justify-center">
                      +{profileList.length - 3}
                    </div>
                  )}
                </div>
              ) : (
                <Avatar className="h-10 w-10 border">
                  {primaryProfile?.avatar ? (
                    <AvatarImage src={primaryProfile.avatar} alt={primaryProfile.name} />
                  ) : (
                    <AvatarFallback>{initials(primaryProfile?.name)}</AvatarFallback>
                  )}
                </Avatar>
              )}
              <div>
                <p className="text-sm font-semibold leading-tight">{profileLabel}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {platformBadges.map((plat) => (
                    <Badge key={plat} variant="secondary" className="text-[11px] uppercase tracking-wide">
                      {plat.toLowerCase()}
                    </Badge>
                  ))}
                  <Badge className={cn('text-[11px]', statusBadge)}>{status}</Badge>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{title}</span>
            {scheduleLabel && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-4 w-4" />
                {scheduleLabel}
              </span>
            )}
            {queueLabel && (
              <span className="inline-flex items-center gap-1">
                <ListChecks className="h-4 w-4" />
                {queueLabel}
              </span>
            )}
            {hasValidationErrors && (
              <span className="inline-flex items-center gap-1 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                {validationMessage || 'Resolve validation issues'}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          {showSaveDraft && onSaveDraft && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSaveDraft}
              disabled={isSubmitting}
            >
              Save Draft
            </Button>
          )}
          <Button
            size="sm"
            onClick={onPublish}
            disabled={disablePublish || isSubmitting}
            className="min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Publish'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CreatePostCommandBar
